import React, { useState, useMemo } from 'react'
import { Menu as MenuIcon, Security, Warning, WhatsApp, Email, Business, Check, SwapHoriz, ContentCopy, CheckCircle, RestartAlt } from '@mui/icons-material';
import { FlexBetween } from '../FlexBetween';
import { setMode } from '../../state/global/globalSlice';
import { AppBar, Box, IconButton, Toolbar, useTheme, Chip, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PanelLeftOpen } from 'lucide-react';
import { Logger } from "../../utils/Logger";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from "../../state/store";
import { LogoutButton } from '../../modules/LogOut';
import { SiteLogo } from '../SiteLogo';
import { ThemeModeToggle } from '../ThemeModeToggle/ThemeModeToggle';
import { ProductPlan, Company } from '../../modules/Interfaces/Company/Company';
import { switchCompanyAsync } from '../../state/authentication/authenticationSlice';
import type { CompanyMembership } from '../../modules/Interfaces/Company/User';
import { useNavigate } from 'react-router-dom';
import { apiClient } from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { fetchBotStatus } from 'state/botStatus/botStatusSlice';

// Custom hook to optimize WhatsApp badge re-renders
const useWhatsAppBadgeData = () => {
    return useSelector((state: RootState) => ({
        deducedStatus: state.botStatus.whatsappDeducedStatus,
        isQRCodeOutdated: state.botStatus.isQRCodeOutdated,
        phoneNumber: state.botStatus.botStatus?.whatsapp?.phoneNumber,
        loading: state.botStatus.loading,
        hasConfig: !!state.botStatus.botStatus?.whatsapp // Track if we have any config data
    }), (left, right) => 
        left.deducedStatus === right.deducedStatus &&
        left.isQRCodeOutdated === right.isQRCodeOutdated &&
        left.phoneNumber === right.phoneNumber &&
        left.loading === right.loading &&
        left.hasConfig === right.hasConfig
    );
};

// Helper function to calculate days remaining
const getDaysRemaining = (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

// Helper function to get license status color
const getLicenseStatusColor = (daysRemaining: number, theme: any) => {
    if (daysRemaining < 0) return theme.palette.error.main; // Expired
    if (daysRemaining <= 7) return theme.palette.warning.main; // Warning (7 days or less)
    if (daysRemaining <= 30) return theme.palette.info.main; // Info (30 days or less)
    return theme.palette.success.main; // Good (more than 30 days)
};

// Helper function to get license status text
const getLicenseStatusText = (daysRemaining: number): string => {
    if (daysRemaining < 0) return 'Expired';
    if (daysRemaining === 0) return 'Expires today';
    if (daysRemaining === 1) return 'Expires tomorrow';
    if (daysRemaining <= 7) return `${daysRemaining} days left`;
    if (daysRemaining <= 30) return `${daysRemaining} days left`;
    return `${daysRemaining} days left`;
};

// Helper functions for WhatsApp badge (moved outside component to prevent recreation)
const getStatusColor = (status: string, theme: any) => {
    switch (status) {
        case 'QRCODE':
            return theme.palette.success.main;
        case 'ONLINE':
            return theme.palette.success.main;
        case 'BUSY':
            return theme.palette.warning.main;
        case 'OFFLINE':
        default:
            return theme.palette.error.main;
    }
};

const getTooltipText = (status: string, phoneNumber?: string, isOutdated?: boolean) => {
    if (isOutdated && status !== 'OFFLINE') return 'QR Code is outdated. Please refresh.';
    switch (status) {
        case 'QRCODE':
            return 'QR Code is ready for scanning. Click to open WhatsApp settings.';
        case 'ONLINE':
            return phoneNumber ? `WhatsApp Bot Online (${phoneNumber})` : 'WhatsApp Bot Online';
        case 'BUSY':
            return 'WhatsApp Bot is busy';
        case 'OFFLINE':
        default:
            return 'WhatsApp Bot is offline. Click to reset the BOT or open Bot Manager.';
    }
};

const STANDARD_WHATSAPP_BOT_TYPE = 'freeWhatsAppBot';

// WhatsApp Bot Status Badge Component
const WhatsAppBotBadge: React.FC = React.memo(() => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { deducedStatus, isQRCodeOutdated, phoneNumber, loading, hasConfig } = useWhatsAppBadgeData();
    const company = useSelector((state: RootState) => state.authentication.company);
    const user = useSelector((state: RootState) => state.authentication.user);
    const { hasInstantUpdateAccess, isUserSuperAdmin } = useModulePermissions();
    const [waMenuAnchor, setWaMenuAnchor] = useState<null | HTMLElement>(null);
    const [waRebooting, setWaRebooting] = useState(false);

    const canUseBotReset =
        (hasInstantUpdateAccess('admin') || isUserSuperAdmin) &&
        company?.configurations?.defaultBotManager !== 'ClientManaged';

    // Only show loading if we don't have any config data (initial load)
    const shouldShowLoading = loading && !hasConfig;

    // Memoize the status data with stable dependencies
    const statusData = useMemo(() => {
        return {
            statusColor: getStatusColor(deducedStatus, theme),
            tooltipText: getTooltipText(deducedStatus, phoneNumber, isQRCodeOutdated)
        };
    }, [deducedStatus, phoneNumber, isQRCodeOutdated, theme.palette.mode]); // Only depend on theme mode, not entire palette

    // Memoize the loading chip to prevent recreation
    const loadingChip = useMemo(() => (
        <Chip
            icon={<WhatsApp />}
            label="..."
            size="small"
            sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                '& .MuiChip-icon': {
                    color: theme.palette.text.secondary
                }
            }}
        />
    ), [theme.palette.mode]); // Only depend on theme mode

    const isQrCodeClickable = deducedStatus === 'QRCODE';
    const chipClickable = isQrCodeClickable || deducedStatus === 'OFFLINE';

    const handleWhatsAppChipClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (deducedStatus === 'QRCODE') {
            navigate('/settings/whatsapp');
        } else if (deducedStatus === 'OFFLINE') {
            setWaMenuAnchor(e.currentTarget);
        }
    };

    const handleResetStandardBot = async () => {
        if (!user?.companyId) return;
        try {
            setWaRebooting(true);
            await apiClient.patch(`/iu/botmanager/updateconfigurations`, {
                botType: STANDARD_WHATSAPP_BOT_TYPE,
                targetStatus: 'restart',
            });
            setWaMenuAnchor(null);
            await dispatch(fetchBotStatus(user.companyId));
        } catch (err) {
            Logger.error('Navbar.tsx', `Reset Standard BOT failed: ${err}`);
        } finally {
            setWaRebooting(false);
        }
    };

    const closeWaMenu = () => setWaMenuAnchor(null);

    if (shouldShowLoading) {
        return loadingChip;
    }

    return (
        <>
            <Chip
                icon={<WhatsApp />}
                label={deducedStatus}
                size="small"
                title={statusData.tooltipText}
                clickable={chipClickable}
                onClick={chipClickable ? handleWhatsAppChipClick : undefined}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${statusData.statusColor}`,
                    color: statusData.statusColor,
                    fontWeight: 'medium',
                    minWidth: '80px', // Fixed minimum width to prevent layout shifts
                    cursor: chipClickable ? 'pointer' : 'default',
                    '& .MuiChip-icon': {
                        color: statusData.statusColor
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover
                    }
                }}
            />
            <Menu
                anchorEl={waMenuAnchor}
                open={Boolean(waMenuAnchor)}
                onClose={closeWaMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {canUseBotReset && (
                    <MenuItem
                        onClick={() => {
                            void handleResetStandardBot();
                        }}
                        disabled={waRebooting}
                        dense
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            {waRebooting ? <CircularProgress size={18} color="inherit" /> : <RestartAlt fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary="Reset Standard BOT" />
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        closeWaMenu();
                        navigate('/settings/bot-manager');
                    }}
                    dense
                >
                    <ListItemText primary="Open Bot Manager" />
                </MenuItem>
            </Menu>
        </>
    );
});

// Add display name for debugging
WhatsAppBotBadge.displayName = 'WhatsAppBotBadge';

// Custom hook to optimize Email badge re-renders
const useEmailBadgeData = () => {
    return useSelector((state: RootState) => ({
        deducedStatus: state.botStatus.emailDeducedStatus,
        loading: state.botStatus.loading,
        hasConfig: !!state.botStatus.botStatus?.email // Track if we have any config data
    }), (left, right) => 
        left.deducedStatus === right.deducedStatus &&
        left.loading === right.loading &&
        left.hasConfig === right.hasConfig
    );
};

// Helper functions for Email badge
const getEmailStatusColor = (status: string, theme: any) => {
    switch (status) {
        case 'ONLINE':
            return theme.palette.success.main;
        case 'OFFLINE':
        default:
            return theme.palette.error.main;
    }
};

const getEmailTooltipText = (status: string) => {
    switch (status) {
        case 'ONLINE':
            return 'Email Service Online';
        case 'OFFLINE':
        default:
            return 'Email Service Offline';
    }
};

// Email Bot Status Badge Component
const EmailBotBadge: React.FC = React.memo(() => {
    const theme = useTheme();
    const { deducedStatus, loading, hasConfig } = useEmailBadgeData();

    // Only show loading if we don't have any config data (initial load)
    const shouldShowLoading = loading && !hasConfig;

    // Memoize the status data with stable dependencies
    const statusData = useMemo(() => {
        return {
            statusColor: getEmailStatusColor(deducedStatus, theme),
            tooltipText: getEmailTooltipText(deducedStatus)
        };
    }, [deducedStatus, theme.palette.mode]); // Only depend on theme mode, not entire palette

    // Memoize the loading chip to prevent recreation
    const loadingChip = useMemo(() => (
        <Chip
            icon={<Email />}
            label="..."
            size="small"
            sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                '& .MuiChip-icon': {
                    color: theme.palette.text.secondary
                }
            }}
        />
    ), [theme.palette.mode]); // Only depend on theme mode

    // Memoize the main chip to prevent recreation
    const mainChip = useMemo(() => (
        <Chip
            icon={<Email />}
            label={deducedStatus}
            size="small"
            title={statusData.tooltipText}
            sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${statusData.statusColor}`,
                color: statusData.statusColor,
                fontWeight: 'medium',
                minWidth: '80px', // Fixed minimum width to prevent layout shifts
                '& .MuiChip-icon': {
                    color: statusData.statusColor
                },
                '&:hover': {
                    backgroundColor: theme.palette.action.hover
                }
            }}
        />
    ), [deducedStatus, statusData.statusColor, statusData.tooltipText, theme.palette.mode]);

    if (shouldShowLoading) {
        return loadingChip;
    }

    return mainChip;
});

// Add display name for debugging
EmailBotBadge.displayName = 'EmailBotBadge';

const CompanySwitcher: React.FC = React.memo(() => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const activeCompanyId = useSelector((state: RootState) => state.authentication.activeCompanyId);
    const companiesList = useSelector((state: RootState) => state.authentication.companiesList);
    const company = useSelector((state: RootState) => state.authentication.company);
    const user = useSelector((state: RootState) => state.authentication.user);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [copied, setCopied] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const effectiveCompanyId = activeCompanyId || user?.companyId;
    if (!effectiveCompanyId) return null;

    const hasMultiple = companiesList.length > 1;
    const activeEntry = companiesList.find((c: CompanyMembership) => c.companyId === effectiveCompanyId);
    const displayName = company?.name || activeEntry?.companyName || effectiveCompanyId;

    const filteredCompanies = useMemo(() => {
        if (!filterText.trim()) return companiesList;
        const q = filterText.toLowerCase();
        return companiesList.filter((c: CompanyMembership) =>
            c.companyName.toLowerCase().includes(q) || c.companyId.toLowerCase().includes(q)
        );
    }, [companiesList, filterText]);

    const handleChipClick = (e: React.MouseEvent<HTMLElement>) => {
        if (hasMultiple) {
            setAnchorEl(e.currentTarget);
            setFilterText('');
        } else {
            navigator.clipboard.writeText(String(effectiveCompanyId)).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }).catch(() => {});
        }
    };

    const handleSwitchCompany = (companyId: string) => {
        setAnchorEl(null);
        setFilterText('');
        if (companyId !== effectiveCompanyId) {
            dispatch(switchCompanyAsync(companyId)).then(() => {
                navigate('/dashboard');
            });
        }
    };

    const handleCopyId = (e: React.MouseEvent, companyId: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(companyId).then(() => {
            setCopiedId(companyId);
            setTimeout(() => setCopiedId(null), 1500);
        }).catch(() => {});
    };

    return (
        <>
            <Chip
                icon={hasMultiple ? <SwapHoriz sx={{ fontSize: 16 }} /> : undefined}
                label={copied ? 'Copied' : (hasMultiple ? displayName : String(effectiveCompanyId))}
                size="small"
                onClick={handleChipClick}
                sx={{
                    maxWidth: 200,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.navigation?.companyChipTextColor ?? theme.palette.primary.contrastText,
                    fontWeight: 'medium',
                    cursor: 'pointer',
                    '& .MuiChip-icon': {
                        color: theme.palette.navigation?.companyChipTextColor ?? theme.palette.primary.contrastText,
                    },
                    '& .MuiChip-label': {
                        color: theme.palette.navigation?.companyChipTextColor ?? theme.palette.primary.contrastText,
                    },
                    '&:hover': { opacity: 0.9 },
                }}
            />
            {hasMultiple && (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => { setAnchorEl(null); setFilterText(''); }}
                    slotProps={{ paper: { sx: { minWidth: 300, maxWidth: 380, maxHeight: 420 } } }}
                >
                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Filter companies..."
                            variant="outlined"
                            color="secondary"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            autoFocus
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                sx: { fontSize: '0.85rem', height: 36 },
                            }}}
                        />
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    {filteredCompanies.length === 0 && (
                        <MenuItem disabled>
                            <Typography variant="caption" color="text.secondary">No companies match your filter</Typography>
                        </MenuItem>
                    )}
                    {filteredCompanies.map((c: CompanyMembership) => {
                        const isActive = c.companyId === effectiveCompanyId;
                        const isHovered = hoveredId === c.companyId;
                        return (
                            <MenuItem
                                key={c.companyId}
                                selected={isActive}
                                onMouseEnter={() => setHoveredId(c.companyId)}
                                onMouseLeave={() => setHoveredId(null)}
                                onClick={() => handleSwitchCompany(c.companyId)}
                                sx={{
                                    py: 0.75, px: 1.5,
                                    borderLeft: isActive ? `3px solid ${theme.palette.success.main}` : '3px solid transparent',
                                    transition: 'border-color 0.15s ease',
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32, position: 'relative' }}>
                                    {isActive ? (
                                        <CheckCircle sx={{ fontSize: 20, color: theme.palette.success.main }} />
                                    ) : isHovered ? (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleSwitchCompany(c.companyId); }}
                                            title="Switch to this company"
                                            sx={{
                                                p: 0,
                                                color: theme.palette.secondary.main,
                                                '&:hover': { color: theme.palette.secondary.dark },
                                            }}
                                        >
                                            <Check sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    ) : (
                                        <Business sx={{ fontSize: 20, color: 'text.secondary' }} />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="body2" noWrap sx={{ fontWeight: isActive ? 700 : 400 }}>
                                                {c.companyName}
                                            </Typography>
                                            {c.isMaster && (
                                                <Chip label="Primary" size="small" color="warning" variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, ml: 0.5,
                                                        borderColor: theme.palette.warning.main,
                                                        color: theme.palette.warning.main,
                                                    }} />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                                            {c.companyId}
                                        </Typography>
                                    }
                                />
                                <Box sx={{
                                    ml: 0.5,
                                    opacity: isHovered || copiedId === c.companyId ? 1 : 0,
                                    transition: 'opacity 0.15s ease',
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleCopyId(e, c.companyId)}
                                        title={copiedId === c.companyId ? 'Copied!' : 'Copy Company ID'}
                                        sx={{ p: 0.5 }}
                                    >
                                        {copiedId === c.companyId
                                            ? <Check sx={{ fontSize: 16, color: theme.palette.success.main }} />
                                            : <ContentCopy sx={{ fontSize: 16, color: 'text.secondary' }} />}
                                    </IconButton>
                                </Box>
                            </MenuItem>
                        );
                    })}
                </Menu>
            )}
        </>
    );
});
CompanySwitcher.displayName = 'CompanySwitcher';

// Role Badge Component with Popup Menu
const RoleBadge: React.FC = () => {
    const theme = useTheme();
    const user = useSelector((state: RootState) => state.authentication.user);
    const company = useSelector((state: RootState) => state.authentication.company);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    
    if (!user?.moduleAndRole || user.moduleAndRole.length === 0) {
        return null;
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    // Check if user is SUPER ADMIN (by email domain)
    const isSuperAdmin = user.email.includes('@campaignking.ai') || 
                        user.email.includes('@sharadtech.com') || 
                        user.email.includes('@scenicmediahub.com');

    const getRoleColor = (role: string) => {
        const roleLower = role.toLowerCase();
        switch (roleLower) {
            case 'admin':
                return theme.palette.warning.main;
            case 'user':
                return theme.palette.success.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    // Get license info for a module
    const getLicenseInfo = (moduleName: string) => {
        if (!company?.productPlans) return null;
        
        const plan = company.productPlans.find((plan: ProductPlan) => 
            plan.module === moduleName && plan.status === 'active'
        );
        
        if (!plan) return null;
        
        const daysRemaining = getDaysRemaining(plan.endsAt);
        return {
            daysRemaining,
            statusColor: getLicenseStatusColor(daysRemaining, theme),
            statusText: getLicenseStatusText(daysRemaining),
            licenseType: plan.licenseType,
            endDate: plan.endsAt
        };
    };

    // Check if any module has expiring license (7 days or less)
    const hasExpiringLicense = user.moduleAndRole.some((moduleRole: { module: string; role: string }) => {
        const licenseInfo = getLicenseInfo(moduleRole.module);
        return licenseInfo && licenseInfo.daysRemaining <= 7;
    });

    return (
        <>
            <IconButton onClick={handleClick}>
                <Badge 
                    color="warning" 
                    variant="dot" 
                    invisible={!hasExpiringLicense}
                    sx={{
                        '& .MuiBadge-dot': {
                            backgroundColor: theme.palette.warning.main,
                        }
                    }}
                >
                    <Security sx={{ fontSize: "25px"}}/>
                </Badge>
            </IconButton>
            
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        minWidth: 300,
                        mt: 1,
                        boxShadow: theme.shadows[8],
                        border: `1px solid ${theme.palette.divider}`,
                    }
                }}
            >
                {/* Header */}
                <MenuItem sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.navigation?.moduleAccessTitleColor ?? theme.palette.secondary.main,
                    fontWeight: 'bold',
                    cursor: 'default',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                    }
                }}>
                    <ListItemText 
                        primary="Module Access & Roles" 
                        primaryTypographyProps={{
                            fontSize: '0.9rem',
                            color: theme.palette.navigation?.moduleAccessTitleColor ?? theme.palette.secondary.main,
                        }}
                    />
                </MenuItem>
                
                {/* SUPER ADMIN Badge */}
                {isSuperAdmin && (
                    <MenuItem sx={{ 
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                        fontWeight: 'bold',
                        cursor: 'default',
                        '&:hover': {
                            backgroundColor: theme.palette.error.main,
                        }
                    }}>
                        <ListItemIcon>
                            <Security sx={{ color: 'inherit', fontSize: '1rem' }} />
                        </ListItemIcon>
                        <ListItemText 
                            primary="SUPER ADMIN" 
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                    </MenuItem>
                )}
                
                {/* Module Roles */}
                {user.moduleAndRole.map((moduleRole: { module: string; role: string }, index: number) => {
                    const licenseInfo = getLicenseInfo(moduleRole.module);
                    
                    return (
                        <MenuItem 
                            key={index}
                            sx={{ 
                                py: 1,
                                borderBottom: index < user.moduleAndRole!.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                            }}
                        >
                            <ListItemIcon>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: getRoleColor(moduleRole.role),
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText 
                                primary={moduleRole.module}
                                secondary={
                                    <Box>
                                        <Typography 
                                            variant="body2" 
                                            color={getRoleColor(moduleRole.role)}
                                            sx={{ fontSize: '0.8rem' }}
                                        >
                                            Role: {moduleRole.role.toUpperCase()}
                                        </Typography>
                                        {licenseInfo && (
                                            <Box sx={{ mt: 0.5 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    color={licenseInfo.statusColor}
                                                    sx={{ 
                                                        fontSize: '0.75rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}
                                                >
                                                    {licenseInfo.daysRemaining <= 7 && (
                                                        <Warning sx={{ fontSize: '0.7rem' }} />
                                                    )}
                                                    {licenseInfo.statusText}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.7rem' }}
                                                >
                                                    {licenseInfo.licenseType} • Expires: {new Date(licenseInfo.endDate).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                }
                                primaryTypographyProps={{ 
                                    fontSize: '0.9rem',
                                    fontWeight: 'medium'
                                }}
                                secondaryTypographyProps={{ 
                                    component: 'div'
                                }}
                            />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

// Mobile Menu Dialog Component
const MobileMenuDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const themePreference = useSelector((state: RootState) => state.global.mode);

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    mt: 8,
                    mx: 2,
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                textAlign: 'center',
                fontWeight: 'bold'
            }}>
                Menu Options
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {/* WhatsApp Bot Status */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        WhatsApp Bot Status
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <WhatsAppBotBadge />
                    </Box>
                </Box>

                {/* Email Service Status */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Email Service Status
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <EmailBotBadge />
                    </Box>
                </Box>

                {/* Role Badge */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Module Access & Roles
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RoleBadge />
                    </Box>
                </Box>

                {/* Theme Toggle */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Theme
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <ThemeModeToggle
                            preference={themePreference}
                            onCycle={() => dispatch(setMode())}
                        />
                    </Box>
                </Box>

                {/* Logout */}
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Account
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <LogoutButton/>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="secondary" fullWidth>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface NavbarProps {
    setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isSidebarOpen: boolean;
    isNonMobile: boolean;
}

export const Navbar: React.FC<NavbarProps> = React.memo(({setIsSidebarOpen, isSidebarOpen, isNonMobile}) => {
    const loggerMode = useSelector((state: RootState) => state.logger.value);
    const dispatch = useDispatch();
    const theme = useTheme();
    const company: Company | null = useSelector((state: RootState) => state.authentication.company);
    const themePreference = useSelector((state: RootState) => state.global.mode);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    Logger.info("st-ck-client/src/components/Navbar/Navbar.tsx","Navbar rendered");

    const bufferLikeToDataUrl = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === 'string') return value;
        if (value?.type === 'Buffer' && Array.isArray(value?.data)) {
            try {
                const u8 = new Uint8Array(value.data as number[]);
                let binary = '';
                for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);
                const base64 = btoa(binary);
                return `data:image/png;base64,${base64}`;
            } catch (e) {
                Logger.error('Navbar', 'Failed to convert company logo buffer to data URL');
                return undefined;
            }
        }
        return undefined;
    };

    const brandLogoSrc: string | undefined = (() => {
        // Only use the theme-specific logo; do not fallback to the opposite theme
        const selected = theme.palette.mode === 'dark' ? company?.brand?.logo?.dark : company?.brand?.logo?.light;
        return bufferLikeToDataUrl(selected);
    })();
    
    return (
        <>
            <AppBar sx={{
                position: 'sticky',
                background: theme.palette.background.default,
                boxShadow: 'none',
                width: '100%',
                top: 0,
                height: '100px'
            }}>
                <Toolbar sx={{
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: { xs: 1, sm: 2 },
                    height: '100px',
                    minHeight: '100px'
                }}>
                    {/* LEFT SIDE */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flex: isNonMobile && !isSidebarOpen ? 1 : 'none'
                    }}>
                        {/* Desktop: Show toggle button only when sidebar is closed */}
                        {isNonMobile && !isSidebarOpen && (
                            <IconButton 
                                onClick={() => setIsSidebarOpen(true)} 
                                sx={{ 
                                    marginRight: 1
                                }}
                            >
                                <PanelLeftOpen />
                            </IconButton>
                        )}
                        
                        {/* Mobile: Always show toggle button */}
                        {!isNonMobile && (
                            <IconButton 
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                                sx={{ 
                                    marginLeft: { xs: "-0.5rem", sm: "-1.5rem" },
                                    marginRight: { xs: 1, sm: 0.5 }
                                }}
                            >
                                <PanelLeftOpen />
                            </IconButton>
                        )}
                        
                        {/* Desktop: SiteLogo positioned right after toggle button */}
                        {isNonMobile && (
                            <Box sx={{ marginLeft: isSidebarOpen ? 0.5 : 0, display: 'flex', alignItems: 'center' }}>
                                <Box component="a" href="/#/dashboard" sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                                    <SiteLogo varient='small'/>
                                </Box>
                                {brandLogoSrc && (
                                    <>
                                        <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                                        <Box component="a" href="/#/dashboard" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <Box component="img" src={brandLogoSrc} alt="Company Logo" sx={{ height: 80, width: 'auto', objectFit: 'contain' }} />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* CENTER - Mobile Only */}
                    {!isNonMobile && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            flex: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box component="a" href="/#/dashboard" sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                                    <SiteLogo varient='small'/>
                                </Box>
                                {brandLogoSrc && (
                                    <>
                                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                        <Box component="a" href="/#/dashboard" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <Box component="img" src={brandLogoSrc} alt="Company Logo" sx={{ height: 80, width: 'auto', objectFit: 'contain' }} />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* RIGHT SIDE */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Desktop: All items visible */}
                        {isNonMobile && (
                            <FlexBetween gap="1.5rem">
                                <CompanySwitcher />
                                <WhatsAppBotBadge />
                                <EmailBotBadge />
                                <RoleBadge />
                                <ThemeModeToggle
                                    preference={themePreference}
                                    onCycle={() => dispatch(setMode())}
                                />
                                <LogoutButton/>
                            </FlexBetween>
                        )}
                        
                        {/* Mobile: Burger menu only */}
                        {!isNonMobile && (
                            <IconButton 
                                onClick={() => setMobileMenuOpen(true)}
                                sx={{ marginLeft: 'auto' }}
                            >
                                <MenuIcon sx={{ fontSize: "25px"}}/>
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile Menu Dialog */}
            <MobileMenuDialog 
                open={mobileMenuOpen} 
                onClose={() => setMobileMenuOpen(false)} 
            />
        </>
    )
});

// Add display name for debugging
Navbar.displayName = 'Navbar';